import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import cx from 'classnames';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Cross from '../Icons/Cross';
import { getIsRtl } from '../utils';
import Tooltip from '../Tooltip';
import makeStylesFn from './styles';
import { myTooltipRenderer, myTooltipRendererSrOnly, showMultiLinedTooltip, shownTooltip } from './utils';
import useKeyboardEscapeHandler from '../hooks/useKeybordEscapeHandler';

var useStyles = makeStyles(makeStylesFn);

var CustomChip = function CustomChip(_ref) {
  var _cx;

  var id = _ref.id,
      parentId = _ref.parentId,
      _ref$items = _ref.items,
      items = _ref$items === undefined ? [] : _ref$items,
      label = _ref.label,
      onDelete = _ref.onDelete,
      renderer = _ref.renderer,
      chipClasses = _ref.chipClasses,
      iconClasses = _ref.iconClasses,
      labelClasses = _ref.labelClasses,
      _ref$ariaLabel = _ref.ariaLabel,
      ariaLabel = _ref$ariaLabel === undefined ? 'delete' : _ref$ariaLabel,
      _ref$testId = _ref.testId,
      testId = _ref$testId === undefined ? 'deleteChip-test-id' : _ref$testId,
      _ref$labelRenderer = _ref.labelRenderer,
      labelRenderer = _ref$labelRenderer === undefined ? R.prop('label') : _ref$labelRenderer,
      ariaLabelTooltip = _ref.ariaLabelTooltip,
      deprecated = _ref.deprecated;

  var theme = useTheme();
  var isRtl = getIsRtl(theme);
  var classes = useStyles();
  var mapIndexed = R.addIndex(R.map);
  var onChange = R.is(Function)(onDelete) ? function () {
    return onDelete(parentId, R.isNil(id) ? [] : R.append(id, deprecated || []));
  } : null;
  var hasTooltip = shownTooltip(items);
  var hasMultiLinedTooltip = showMultiLinedTooltip(items);
  var multiLinedTooltipRenderer = function multiLinedTooltipRenderer(arr) {
    return mapIndexed(function (item, index) {
      return React.createElement(
        'div',
        { key: index },
        myTooltipRenderer({ isRtl: isRtl, labelRenderer: labelRenderer })(item),
        React.createElement('br', null)
      );
    })(R.prop('parents')(R.head(arr)));
  };

  var multiLinedTooltipRendererSrOnly = function multiLinedTooltipRendererSrOnly(items) {
    return R.join(',')(R.map(myTooltipRendererSrOnly({ isRtl: isRtl, labelRenderer: labelRenderer, ariaLabelTooltip: ariaLabelTooltip }), R.propOr([], 'parents')(R.head(items))));
  };

  var _useKeyboardEscapeHan = useKeyboardEscapeHandler(),
      open = _useKeyboardEscapeHan.open,
      setOpen = _useKeyboardEscapeHan.setOpen;

  return React.createElement(Chip, {
    'data-testid': testId,
    size: 'small',
    'aria-label': label,
    tabIndex: R.isNil(onDelete) ? 0 : -1,
    label: hasTooltip || hasMultiLinedTooltip ? React.createElement(
      Tooltip,
      {
        placement: 'top',
        title: hasTooltip ? renderer(items) : hasMultiLinedTooltip ? multiLinedTooltipRenderer(items) : '',
        tabIndex: 0,
        'aria-label': hasTooltip ? myTooltipRendererSrOnly({ isRtl: isRtl, labelRenderer: labelRenderer, ariaLabelTooltip: ariaLabelTooltip })(items) : hasMultiLinedTooltip ? multiLinedTooltipRendererSrOnly(items) : null,
        'aria-hidden': false,
        onOpen: function onOpen() {
          return setOpen(true);
        },
        onClose: function onClose() {
          return setOpen(false);
        },
        disableHoverListener: !hasTooltip && !hasMultiLinedTooltip,
        open: open
      },
      React.createElement(
        'span',
        null,
        '...',
        React.createElement(
          'span',
          { className: classes.childrenIndicator },
          '>'
        ),
        label
      )
    ) : label,
    role: 'presentation',
    deleteIcon: React.createElement(
      IconButton,
      {
        disableRipple: true,
        className: iconClasses,
        'aria-label': ariaLabel + ' ' + label,
        component: 'span',
        tabIndex: 0,
        size: 'small'
      },
      React.createElement(Cross, null)
    ),
    clickable: true,
    onDelete: onChange,
    className: cx(chipClasses, (_cx = {}, _cx[classes.tooltipIndicator] = hasTooltip || hasMultiLinedTooltip, _cx)),
    classes: { label: labelClasses }
  });
};

CustomChip.propTypes = process.env.NODE_ENV !== "production" ? {
  testId: PropTypes.string,
  id: PropTypes.string,
  parentId: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onDelete: PropTypes.func,
  renderer: PropTypes.func,
  chipClasses: PropTypes.string,
  iconClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  items: PropTypes.array,
  labelRenderer: PropTypes.func,
  deprecated: PropTypes.array,
  ariaLabelTooltip: PropTypes.string
} : {};

export default CustomChip;